/**
 * Agent App - Main Application Component with Amazon Connect Integration
 */

import { useState, useEffect, useRef, useCallback } from "react";
import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Alert from "@cloudscape-design/components/alert";
import { NotesTaker } from "../components/notes/NotesTaker";
import { useAppContext, isTokenValidForContact } from "../contexts/AppContext";
import { createNotes, updateContactStatus } from "../lib/api";
import { getAttrValue, getInstanceArn } from "../lib/connect-utils";
import { trackEvent, trackError } from "../lib/analytics";
import type { CalltreeTableItem } from "../lib/types";
import { AttachmentsPanel } from "../components/attachments";
import {
  bootstrapNotesPopoutWindow,
  stopNotesPopoutStream,
  clearStoredPopoutContactId,
  focusNotesPopout,
  getStoredPopoutContactId,
  isC360SearchMessage,
  isNotesPopoutReadyMessage,
  openNotesPopout,
  sendC360StateToPopout,
} from "../lib/notesPopout";

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: "system-ui, -apple-system, sans-serif",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
  },
  notesTaker: {
    flex: 1,
    overflow: "hidden",
  },
  statusMessage: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "16px",
    color: "#6b7280",
    padding: "24px",
    textAlign: "center",
  },
  errorText: {
    fontSize: "14px",
    color: "#ef4444",
  },
  contentArea: {
    padding: "20px",
  },
};

// =============================================================================
// Main Components
// =============================================================================

const calltreeTableColumns = [
  {
    id: "name",
    header: "Name",
    cell: (item: CalltreeTableItem) => item.name || "-",
    isRowHeader: true,
  },
  {
    id: "description",
    header: "Value",
    cell: (item: CalltreeTableItem) => item.description || "-",
  },
];

interface NoteTakerAppProps {
  contactId: string;
  isPopout?: boolean;
  onOpenPopout?: () => void;
  popoutError?: string | null;
  c360Query?: string;
  onC360StateChange?: NotesTakerProps["onC360StateChange"];
  c360SearchRef?: NotesTakerProps["c360SearchRef"];
}

type NotesTakerProps = React.ComponentProps<typeof NotesTaker>;

function NoteTakerApp({
  contactId,
  isPopout = false,
  onOpenPopout,
  popoutError,
  c360Query,
  onC360StateChange,
  c360SearchRef,
}: NoteTakerAppProps) {
  return (
    <div style={styles.container}>
      <div style={styles.notesTaker}>
        <NotesTaker
          contactId={contactId}
          showHeader={true}
          showPopoutButton={!isPopout}
          onPopOut={onOpenPopout}
          c360Query={c360Query}
          onC360StateChange={onC360StateChange}
          c360SearchRef={c360SearchRef}
        />
      </div>
      {popoutError ? (
        <div style={styles.contentArea}>
          <Alert statusIconAriaLabel="Error" type="error">
            {popoutError}
          </Alert>
        </div>
      ) : null}
    </div>
  );
}

function CalltreeTableView({
  items,
  contactId,
  attachmentsEnabled,
}: {
  items: CalltreeTableItem[];
  contactId: string;
  attachmentsEnabled: boolean;
}) {
  return (
    <div style={styles.container}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        <div style={{ ...styles.contentArea, flex: 1, minWidth: 0, overflow: "auto" }}>
          <Container header={<Header variant="h2">AI-Generated Insights</Header>}>
            <Table
              columnDefinitions={calltreeTableColumns}
              items={items}
              variant="embedded"
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No data</b>
                </Box>
              }
            />
          </Container>
        </div>
        {attachmentsEnabled ? <AttachmentsPanel contactId={contactId} /> : null}
      </div>
    </div>
  );
}

// =============================================================================
// Main App Component
// =============================================================================

export default function AgentApp() {
  const {
    contactClient,
    agentClient,
    agentArn,
    initialContactId,
    sdkError,
    isSdkInitializing,
    isAuthenticated,
    authenticate,
    clearAuth,
    isFeatureEnabled,
  } = useAppContext();
  const attachmentsEnabled = isFeatureEnabled("attachments");

  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isCreatingNotes, setIsCreatingNotes] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState<boolean | null>(null);
  const [currentContactId, setCurrentContactId] = useState<string | null>(null);
  const [calltreeTable, setCalltreeTable] = useState<CalltreeTableItem[]>([]);
  const [c360Query, setC360Query] = useState<string | undefined>(undefined);
  const [notesWindowOpen, setNotesWindowOpen] = useState(false);
  const [popoutError, setPopoutError] = useState<string | null>(null);
  const notesWindowRef = useRef<Window | null>(null);
  const c360StateRef = useRef<Parameters<typeof sendC360StateToPopout>[1] | null>(null);
  const c360SearchRef = useRef<((query: string) => void) | null>(null);

  // Check if page is embedded in iframe
  useEffect(() => {
    setIsEmbedded(window.self !== window.top);
  }, []);

  const handleOpenPopout = useCallback(() => {
    if (!currentContactId) {
      return;
    }

    if (focusNotesPopout(notesWindowRef.current)) {
      setPopoutError(null);
      setNotesWindowOpen(true);
      return;
    }

    const popoutWindow = openNotesPopout(currentContactId);
    if (!popoutWindow) {
      setPopoutError("Your browser blocked the notepad pop-out window. Please allow pop-ups and try again.");
      setNotesWindowOpen(false);
      return;
    }

    const bootstrapped = bootstrapNotesPopoutWindow(popoutWindow, currentContactId, agentArn);
    if (!bootstrapped) {
      setPopoutError("Notepad authentication is still initializing. Try opening the window again in a moment.");
      setNotesWindowOpen(false);
      return;
    }

    notesWindowRef.current = popoutWindow;
    setPopoutError(null);
    setNotesWindowOpen(true);
  }, [currentContactId]);

  // Forward C360 state to the popout window so it doesn't invoke its own Lambda.
  const handleC360StateChange = useCallback(
    (state: Parameters<typeof sendC360StateToPopout>[1]) => {
      c360StateRef.current = state;
      sendC360StateToPopout(notesWindowRef.current, state);
    },
    [],
  );

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (isNotesPopoutReadyMessage(event.data) && currentContactId) {
        const childWindow = event.source;
        if (childWindow && "postMessage" in childWindow) {
          notesWindowRef.current = childWindow as Window;
          bootstrapNotesPopoutWindow(childWindow as Window, currentContactId, agentArn);
          if (c360StateRef.current) {
            sendC360StateToPopout(childWindow as Window, c360StateRef.current);
          }
          setPopoutError(null);
          setNotesWindowOpen(true);
        }
      }

      if (isC360SearchMessage(event.data)) {
        c360SearchRef.current?.(event.data.query);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [currentContactId, isEmbedded]);

  useEffect(() => {
    if (!notesWindowOpen) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (notesWindowRef.current?.closed) {
        notesWindowRef.current = null;
        setNotesWindowOpen(false);
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [notesWindowOpen]);

  useEffect(() => {
    if (!isEmbedded || !currentContactId || !isAuthenticated || isCreatingNotes) {
      return;
    }

    if (!isFeatureEnabled("notepad")) {
      return;
    }

    const currentPopoutContactId = getStoredPopoutContactId();
    if (currentPopoutContactId !== currentContactId || !notesWindowRef.current || notesWindowRef.current.closed) {
      handleOpenPopout();
    } else {
      notesWindowRef.current.location.replace(
        `${window.location.origin}/notepad?contactId=${encodeURIComponent(currentContactId)}`
      );
      bootstrapNotesPopoutWindow(notesWindowRef.current, currentContactId, agentArn);
      setNotesWindowOpen(true);
    }
  }, [currentContactId, handleOpenPopout, isAuthenticated, isCreatingNotes, isEmbedded, isFeatureEnabled]);

  useEffect(() => {
    if (!currentContactId) {
      clearStoredPopoutContactId();
      notesWindowRef.current = null;
      setNotesWindowOpen(false);
      setPopoutError(null);
    }
  }, [currentContactId]);

  // Run the production auth + note-bootstrap flow for a given contact.
  // Uses the SDK clients from context to read contact attrs and the agent ARN.
  const authenticateWithContact = useCallback(
    async (contactId: string) => {
      if (!contactClient || !agentClient) return;
      try {
        setIsAuthenticating(true);
        setAuthError(null);

        const attributes = await contactClient.getAttributes(contactId, "*");

        // Resolve customer identifier for C360 lookup.
        // Voice contacts → phone (CustomerEndpoint); chat → email attribute.
        try {
          const channelType = await contactClient.getChannelType(contactId);
          const channel = channelType?.type ?? "voice";
          let query: string | undefined;
          if (channel === "chat" || channel === "task" || channel === "email") {
            query = getAttrValue(attributes.email) || getAttrValue(attributes.CustomerEndpoint) || undefined;
          } else {
            // Voice / callback — use the customer phone
            query = getAttrValue(attributes.CustomerEndpoint) || undefined;
          }
          console.log("[C360] resolved customer identifier:", { channel, query });
          setC360Query(query);
        } catch (err) {
          console.warn("[C360] Failed to resolve customer identifier:", err);
        }

        // Reuse existing JWT if still valid for this contact
        if (isTokenValidForContact(contactId)) {
          console.log("[Auth] Reusing existing JWT token");

          // Restore calltree table from contact attributes when notepad is disabled
          if (!isFeatureEnabled("notepad")) {
            const calltreeTableStr = getAttrValue(attributes.calltree_table);
            if (calltreeTableStr) {
              try {
                setCalltreeTable(JSON.parse(calltreeTableStr));
              } catch (err) {
                console.warn("Failed to parse calltree_table:", err);
              }
            }
          }
          setIsAuthenticating(false);
          return;
        }

        // Fresh authentication via HMAC signature
        const agentArn = await agentClient.getARN();
        const signature = getAttrValue(attributes.calltree_agent_app_signature);
        if (!signature) throw new Error("No auth signature in contact attributes");
        if (!agentArn) throw new Error("Failed to get agent ARN");

        // Try instance ARN first (new signing), fall back to agent ARN (legacy signing)
        const instanceArn = getInstanceArn(agentArn);
        try {
          await authenticate(signature, instanceArn, contactId);
        } catch {
          console.log("[Auth] Instance ARN auth failed, falling back to agent ARN");
          await authenticate(signature, agentArn, contactId);
        }
        setIsAuthenticating(false);
        trackEvent("authenticated", { contactId });

        // Parse calltree_table + case_id from contact attributes
        const calltreeTableStr = getAttrValue(attributes.calltree_table);
        let parsedCalltreeTable: CalltreeTableItem[] = [];
        if (calltreeTableStr) {
          try {
            parsedCalltreeTable = JSON.parse(calltreeTableStr);
          } catch (e) {
            console.warn("Failed to parse calltree_table:", e);
          }
        }
        const caseId = getAttrValue(attributes.case_id) || undefined;
        setCalltreeTable(parsedCalltreeTable);

        if (isFeatureEnabled("notepad")) {
          setIsCreatingNotes(true);
          await createNotes(contactId, parsedCalltreeTable, caseId);
          setIsCreatingNotes(false);
          setCalltreeTable([]);
        }
      } catch (error) {
        console.error("Authentication failed:", error);
        setAuthError(error instanceof Error ? error.message : "Authentication failed");
        setIsAuthenticating(false);
        setIsCreatingNotes(false);
        setCalltreeTable([]);
        trackError(error instanceof Error ? error : new Error("Authentication failed"));
      }
    },
    [contactClient, agentClient, authenticate, isFeatureEnabled],
  );

  // Pick up contactId from the initial SDK scope (if launched inside a contact)
  useEffect(() => {
    if (!contactClient || !agentClient || !initialContactId) return;
    authenticateWithContact(initialContactId).then(() => {
      setCurrentContactId(initialContactId);
    });
  }, [contactClient, agentClient, initialContactId, authenticateWithContact]);

  // Attach Connect contact event handlers (production flow)
  useEffect(() => {
    if (!contactClient) return;

    const handleConnected = async (data: { contactId: string }) => {
      trackEvent("contact_connected", { contactId: data.contactId });
      if (!isTokenValidForContact(data.contactId)) {
        clearAuth();
      }
      await authenticateWithContact(data.contactId);
      setCurrentContactId(data.contactId);
    };
    contactClient.onConnected(handleConnected);

    const handleStartingAcw = async (data: { contactId: string }) => {
      if (data.contactId) {
        try {
          await updateContactStatus(data.contactId, "completed");
        } catch (err) {
          console.error("Failed to update contact status:", err);
        }
      }
    };
    contactClient.onStartingAcw(handleStartingAcw);

    const handleCleared = async (data: { contactId: string }) => {
      // Keep the review window open while stopping its independent stream.
      stopNotesPopoutStream(notesWindowRef.current, data.contactId);
      setCurrentContactId(null);
      clearAuth();
    };
    contactClient.onCleared(handleCleared);

    trackEvent("app_initialized", {});
    return () => {
      contactClient.offConnected(handleConnected);
      contactClient.offStartingAcw(handleStartingAcw);
      contactClient.offCleared(handleCleared);
    };
  }, [contactClient, authenticateWithContact, clearAuth]);

  // Still checking if embedded
  if (isEmbedded === null) {
    return null;
  }

  // Show authenticated app
  if (isAuthenticated && currentContactId && !isCreatingNotes) {
    if (isFeatureEnabled("notepad")) {
      return (
        <NoteTakerApp
          contactId={currentContactId}
          isPopout={!isEmbedded}
          onOpenPopout={handleOpenPopout}
          popoutError={popoutError}
          c360Query={c360Query}
          onC360StateChange={handleC360StateChange}
          c360SearchRef={c360SearchRef}
        />
      );
    }
    return (
      <CalltreeTableView
        items={calltreeTable}
        contactId={currentContactId}
        attachmentsEnabled={attachmentsEnabled}
      />
    );
  }

  // Show calltree table at top while initializing notes
  if (isCreatingNotes && calltreeTable.length > 0) {
    return (
      <div style={styles.container}>
        <div style={styles.contentArea}>
          <SpaceBetween size="l">
            <Container header={<Header variant="h2">AI-Generated Insights</Header>}>
              <Table
                columnDefinitions={calltreeTableColumns}
                items={calltreeTable}
                variant="embedded"
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No data</b>
                  </Box>
                }
              />
            </Container>
            <Box textAlign="center" padding={{ vertical: "l" }}>
              <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <Spinner />
                <span>Initializing notepad...</span>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </div>
      </div>
    );
  }

  // Loading/error states
  return (
    <div style={styles.container}>
      <div style={styles.statusMessage}>
        {sdkError ? (
          <>
            <p>
              <strong>SDK Error</strong>
            </p>
            <p style={styles.errorText}>{sdkError}</p>
          </>
        ) : isSdkInitializing ? (
          <p></p>
        ) : isAuthenticating ? (
          <p></p>
        ) : isCreatingNotes ? (
          <p>Initializing notepad...</p>
        ) : authError ? (
          <>
            <p>
              <strong>Authentication Failed</strong>
            </p>
            <p style={styles.errorText}>{authError}</p>
          </>
        ) : !currentContactId ? (
          <p>
            <strong>No active contact</strong>
          </p>
        ) : (
          <p>Waiting for authentication...</p>
        )}
      </div>
    </div>
  );
}
